import React, { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";

const STORAGE_KEY = "lingent_icon_pos";
const DRAG_THRESHOLD = 5; // px

export default function LingentFloatingIcon({ onOpen }) {
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // ignore
    }
    if (typeof window === "undefined") return { x: 0, y: 0 };
    return { x: window.innerWidth - 110, y: window.innerHeight - 140 };
  });
  const [dragging, setDragging] = useState(false);

  // refs that don't trigger re-render
  const startRef = useRef(null); // { pointerX, pointerY, posX, posY }
  const movedRef = useRef(false);
  const buttonRef = useRef(null);

  // clamp inside viewport when window resizes
  useEffect(() => {
    const onResize = () => {
      setPos((p) => ({
        x: Math.min(Math.max(0, p.x), window.innerWidth - 64),
        y: Math.min(Math.max(0, p.y), window.innerHeight - 64),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    buttonRef.current?.setPointerCapture?.(e.pointerId);
    startRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      posX: pos.x,
      posY: pos.y,
    };
    movedRef.current = false;
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!startRef.current) return;
    const dx = e.clientX - startRef.current.pointerX;
    const dy = e.clientY - startRef.current.pointerY;
    if (!movedRef.current && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) {
      movedRef.current = true;
    }
    if (movedRef.current) {
      const nextX = Math.min(
        Math.max(0, startRef.current.posX + dx),
        window.innerWidth - 64
      );
      const nextY = Math.min(
        Math.max(0, startRef.current.posY + dy),
        window.innerHeight - 64
      );
      setPos({ x: nextX, y: nextY });
    }
  };

  const onPointerUp = (e) => {
    if (!startRef.current) return;
    buttonRef.current?.releasePointerCapture?.(e.pointerId);
    setDragging(false);
    const wasDrag = movedRef.current;
    if (wasDrag) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
      } catch (err) {
        // ignore
      }
    } else {
      // treat as tap
      onOpen?.();
    }
    startRef.current = null;
    movedRef.current = false;
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      data-testid="lingent-floating-icon"
      aria-label="Open Lingent composer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`,
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 50,
        touchAction: "none",
        transition: dragging ? "none" : "transform 0.15s ease-out",
      }}
      className={`select-none p-0 bg-transparent border-0 ${
        dragging ? "cursor-grabbing" : "cursor-grab"
      }`}
    >
      <div className="relative">
        <span className="absolute inset-0 rounded-full bg-safety/30 animate-pulse-ring" />
        <div className="relative w-16 h-16 rounded-full bg-safety shadow-[0_8px_24px_rgba(255,95,31,0.45)] flex items-center justify-center border-2 border-ink hover:scale-105 transition-transform">
          <div className="flex flex-col items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            <span className="font-display font-black text-white text-[10px] mt-0.5 tracking-tightest">
              LINGENT
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
